import React, { useRef, useState } from 'react';
import { createMedia, deleteMedia, updateMedia, useMedia, type MediaInput, type MediaKind, type MediaRecord } from '../lib/media';

const emptyForm: MediaInput = {
  kind: 'video',
  title: '',
  label: '',
};

export function AdminMedia() {
  const { items, loading, error: mediaError } = useMedia();
  const [form, setForm] = useState<MediaInput>(emptyForm);
  const [editing, setEditing] = useState<MediaRecord | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const posterRef = useRef<HTMLInputElement>(null);

  const resetFiles = () => {
    if (fileRef.current) fileRef.current.value = '';
    if (posterRef.current) posterRef.current.value = '';
  };

  const saveItem = async (event: React.FormEvent) => {
    event.preventDefault();
    const file = fileRef.current?.files?.[0] ?? null;
    const poster = posterRef.current?.files?.[0] ?? null;

    if (!editing && !file) {
      setError('Choose a video or image to upload.');
      return;
    }

    setBusy(true);
    setError(null);
    setStatus(null);
    setProgress(0);
    try {
      if (editing) {
        await updateMedia(editing, form, file, form.kind === 'video' ? poster : null, setProgress);
        setStatus('Media updated.');
      } else if (file) {
        await createMedia(form, file, form.kind === 'video' ? poster : null, setProgress);
        setStatus('Media added.');
      }
      setForm(emptyForm);
      setEditing(null);
      resetFiles();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save media.');
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <>
      <form className="contact-form admin-form card" onSubmit={saveItem}>
        <p className="eyebrow">{editing ? 'Edit media' : 'Add media'}</p>
        <div className="admin-form-grid">
          <label>
            Type*
            <select
              value={form.kind}
              onChange={(e) => setForm({ ...form, kind: e.target.value as MediaKind })}
            >
              <option value="video">Video</option>
              <option value="image">Image</option>
            </select>
          </label>
          <label>
            Label
            <input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder={form.kind === 'video' ? 'Live cut' : 'Photo'}
            />
          </label>
        </div>
        <label>
          Title*
          <input
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Wasted On You"
          />
        </label>
        <label>
          {form.kind === 'video' ? 'Video file' : 'Image file'}
          {editing ? '' : '*'}
          <input
            ref={fileRef}
            type="file"
            accept={form.kind === 'video' ? 'video/*' : 'image/*'}
            required={!editing}
          />
        </label>
        {form.kind === 'video' && (
          <label>
            Poster image
            <input ref={posterRef} type="file" accept="image/*" />
          </label>
        )}
        {editing && <p className="meta">Leave file fields empty to keep the current upload.</p>}
        {progress !== null && <p className="meta">Uploading… {progress}%</p>}
        <div className="form-actions">
          <button className="hero-btn" type="submit" disabled={busy}>
            {editing ? 'Save changes' : 'Add media'}
          </button>
          {editing && (
            <button
              className="text-link as-button"
              type="button"
              onClick={() => {
                setEditing(null);
                setForm(emptyForm);
                resetFiles();
              }}
            >
              Cancel edit
            </button>
          )}
        </div>
      </form>

      {status && <p className="status">{status}</p>}
      {(error || mediaError) && <p className="status error">{error || mediaError}</p>}

      <div className="admin-list-header">
        <h2>Media library</h2>
      </div>

      {loading ? (
        <p className="meta">Loading media…</p>
      ) : items.length === 0 ? (
        <p className="meta">No videos or photos in the console yet. Add one above.</p>
      ) : (
        <div className="admin-show-list">
          {items.map((item) => (
            <article key={item.id} className="card admin-show-row">
              <div className="admin-media-preview">
                {item.kind === 'image' || item.poster ? (
                  <img src={item.poster || item.src} alt="" />
                ) : (
                  <video src={item.src} muted playsInline preload="metadata" />
                )}
                <div>
                  <p className="eyebrow">{item.label || item.kind}</p>
                  <h3>{item.title}</h3>
                  <p className="meta">{item.kind === 'video' ? 'Video' : 'Image'}</p>
                </div>
              </div>
              <div className="admin-row-actions">
                <button
                  className="text-link as-button"
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setEditing(item);
                    setForm({ kind: item.kind, title: item.title, label: item.label });
                    resetFiles();
                    setStatus(null);
                    setError(null);
                  }}
                >
                  Edit
                </button>
                <button
                  className="text-link as-button"
                  type="button"
                  disabled={busy}
                  onClick={async () => {
                    if (!window.confirm(`Delete ${item.title}?`)) return;
                    setBusy(true);
                    setError(null);
                    try {
                      await deleteMedia(item);
                      if (editing?.id === item.id) {
                        setEditing(null);
                        setForm(emptyForm);
                        resetFiles();
                      }
                      setStatus('Media deleted.');
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Could not delete media.');
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  Delete
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  );
}
